import {
  useEffect,
  useRef,
  useImperativeHandle,
  useLayoutEffect,
  forwardRef,
} from "react";
import { ActiveSelection, Canvas, Textbox, util } from "fabric";
import {
  collectFontFamiliesFromFabricCanvas,
  loadInvitationGoogleFonts,
} from "../../utils/invitationGoogleFonts";
import {
  loadFabricImageForCanvas,
  ensureFabricJsonImagesCrossOrigin,
} from "./invitationFabricUtils";
import { isWebmUrl } from "./invitationMediaUtils";
import {
  InvitationFabricVideo,
  MaxInvitationVideosError,
  countInvitationVideosOnCanvas,
  loadInvitationFabricVideoFromUrl,
  playInvitationVideosOnCanvas,
  registerInvitationFabricVideo,
  sanitizeInvitationCanvasJsonForLoad,
} from "./invitationFabricVideo";

export const CANVAS_WIDTH = 800;
export const CANVAS_HEIGHT = 1100;

const CLIPBOARD_APP = "pingvite-invitation-builder";
const CLIPBOARD_V = 1;
const PASTE_OFFSET = 16;

function isEditableDomTarget(el) {
  if (!el || el.nodeType !== Node.ELEMENT_NODE) return false;
  const tag = el.tagName?.toLowerCase();
  if (tag === "input" || tag === "textarea" || tag === "select") return true;
  return Boolean(el.isContentEditable);
}

function isInsideInvitationBuilder(el) {
  return Boolean(el?.closest?.(".invitation-builder"));
}

/**
 * After clicking the Fabric canvas, focus often stays on document.body, so
 * keydown/paste `target` is not under `.invitation-builder` and shortcuts no-op.
 * Still skip real form fields so we do not steal copy/paste from inputs.
 */
function shouldHandleInvitationBuilderClipboard(e) {
  const target = e.target;
  if (!target) return false;
  if (isEditableDomTarget(target)) return false;

  if (isInsideInvitationBuilder(target)) return true;

  const name = target.nodeName?.toLowerCase();
  if (name === "body" || name === "html") {
    return Boolean(document.querySelector(".invitation-builder"));
  }

  return false;
}

function buildClipboardPayload(objects) {
  return {
    app: CLIPBOARD_APP,
    v: CLIPBOARD_V,
    objects: objects.map((o) => o.toObject()),
  };
}

function isOurClipboardPayload(data) {
  return (
    data &&
    typeof data === "object" &&
    data.app === CLIPBOARD_APP &&
    data.v === CLIPBOARD_V &&
    Array.isArray(data.objects) &&
    data.objects.length > 0
  );
}

function sanitizeCanvasJson(json) {
  if (!json || typeof json !== "object") return json;
  const clone = JSON.parse(JSON.stringify(json));
  const fix = (o) => {
    if (!o || typeof o !== "object") return;
    if (o.stroke === null || o.stroke === undefined) {
      delete o.stroke;
    }
    if (o.objects && Array.isArray(o.objects)) {
      o.objects.forEach(fix);
    }
  };
  fix(clone);
  sanitizeInvitationCanvasJsonForLoad(clone);
  ensureFabricJsonImagesCrossOrigin(clone);
  return clone;
}

function sanitizePastedInvitationObjects(objects) {
  if (!Array.isArray(objects)) return objects;
  const wrap = { objects: JSON.parse(JSON.stringify(objects)) };
  sanitizeInvitationCanvasJsonForLoad(wrap);
  return wrap.objects;
}

function applyReadOnlyToCanvas(canvas) {
  if (!canvas) return;
  canvas.selection = false;
  canvas.skipTargetFind = true;
  canvas.discardActiveObject();
  const walk = (obj) => {
    if (!obj) return;
    const patch = { selectable: false, evented: false };
    if (obj.type === "textbox" || obj.type === "i-text") {
      patch.editable = false;
    }
    obj.set(patch);
    if (typeof obj.forEachObject === "function") {
      obj.forEachObject((child) => walk(child));
    }
  };
  canvas.getObjects().forEach((obj) => walk(obj));
  canvas.requestRenderAll();
}

const CanvasArea = forwardRef(function CanvasArea(
  { onSelectionChange, onCanvasChange, backgroundColor = "#ffffff", onReady, readOnly = false },
  ref
) {
  const canvasEl = useRef(null);
  const fabricRef = useRef(null);
  const clipboardPayloadRef = useRef(null);
  const skipNextPasteEventRef = useRef(false);
  const readOnlyRef = useRef(readOnly);

  /**
   * Store all changing callbacks in a ref.
   * Event handlers inside the one-time useEffect read from this ref so they
   * always have the latest prop values without needing to re-run the effect.
   */
  const cbRef = useRef({ onSelectionChange, onCanvasChange, onReady });
  useLayoutEffect(() => {
    cbRef.current = { onSelectionChange, onCanvasChange, onReady };
  });
  useLayoutEffect(() => {
    readOnlyRef.current = readOnly;
  }, [readOnly]);

  /**
   * Canvas lifecycle — only restarts when backgroundColor changes.
   * All callbacks are accessed through cbRef to avoid stale closures.
   */
  useEffect(() => {
    const el = canvasEl.current;
    if (!el) return undefined;

    const canvas = new Canvas(el, {
      width: CANVAS_WIDTH,
      height: CANVAS_HEIGHT,
      backgroundColor,
      preserveObjectStacking: true,
    });
    fabricRef.current = canvas;
    if (readOnly) {
      canvas.selection = false;
      canvas.skipTargetFind = true;
    }

    registerInvitationFabricVideo();

    cbRef.current.onReady?.();

    const refreshVideoFlags = () => {
      canvas._pingviteHasVideo = countInvitationVideosOnCanvas(canvas) > 0;
    };

    let videoScanScheduled = false;
    const scheduleVideoScan = () => {
      if (videoScanScheduled) return;
      videoScanScheduled = true;
      requestAnimationFrame(() => {
        videoScanScheduled = false;
        refreshVideoFlags();
      });
    };

    canvas.on("object:added", scheduleVideoScan);
    canvas.on("object:removed", scheduleVideoScan);

    let videoRaf = 0;
    const videoRenderLoop = () => {
      videoRaf = requestAnimationFrame(videoRenderLoop);
      if (canvas._pingviteHasVideo && !canvas.destroyed && !canvas.disposed) {
        canvas.renderAll();
      }
    };
    videoRaf = requestAnimationFrame(videoRenderLoop);
    refreshVideoFlags();

    const notifySelection = () => {
      const obj = canvas.getActiveObject() ?? null;
      cbRef.current.onSelectionChange?.(obj);
    };

    const bumpAndNotify = () => {
      cbRef.current.onCanvasChange?.();
      notifySelection();
    };

    canvas.on("selection:created", notifySelection);
    canvas.on("selection:updated", notifySelection);
    canvas.on("selection:cleared", notifySelection);
    canvas.on("object:modified", bumpAndNotify);
    canvas.on("text:editing:entered", bumpAndNotify);
    canvas.on("text:editing:exited", bumpAndNotify);

    const pasteObjects = async (payload) => {
      if (!isOurClipboardPayload(payload)) return;
      try {
        const objects = sanitizePastedInvitationObjects(payload.objects);
        const enlived = await util.enlivenObjects(objects);
        canvas.discardActiveObject();
        enlived.forEach((obj) => {
          obj.set({
            left: (obj.left || 0) + PASTE_OFFSET,
            top: (obj.top || 0) + PASTE_OFFSET,
          });
          canvas.add(obj);
        });
        if (enlived.length === 1) {
          canvas.setActiveObject(enlived[0]);
        } else {
          const sel = new ActiveSelection(enlived, { canvas });
          canvas.setActiveObject(sel);
        }
        canvas.requestRenderAll();
        refreshVideoFlags();
        playInvitationVideosOnCanvas(canvas);
        cbRef.current.onCanvasChange?.();
        cbRef.current.onSelectionChange?.(canvas.getActiveObject() ?? null);
      } catch (err) {
        console.warn("Invitation canvas paste failed", err);
      }
    };

    const onKeyDown = (e) => {
      if (readOnlyRef.current) return;
      if (e.key === "Delete" || e.key === "Backspace") {
        if (!shouldHandleInvitationBuilderClipboard(e)) return;
        const active = canvas.getActiveObject();
        if (!active || active.isEditing) return;
        const objects = canvas.getActiveObjects();
        if (!objects.length) return;
        objects.forEach((obj) => {
          canvas.remove(obj);
        });
        canvas.discardActiveObject();
        canvas.requestRenderAll();
        cbRef.current.onCanvasChange?.();
        cbRef.current.onSelectionChange?.(null);
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      const mod = e.ctrlKey || e.metaKey;
      if (!mod || !shouldHandleInvitationBuilderClipboard(e)) return;

      const key = e.key?.toLowerCase();
      if (key === "c") {
        const objects = canvas.getActiveObjects();
        if (!objects.length) return;
        const active = canvas.getActiveObject();
        if (active?.isEditing) return;

        const payload = buildClipboardPayload(objects);
        clipboardPayloadRef.current = payload;
        if (navigator.clipboard?.writeText) {
          navigator.clipboard.writeText(JSON.stringify(payload)).catch(() => {});
        }
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      if (key === "v") {
        const payload = clipboardPayloadRef.current;
        if (!isOurClipboardPayload(payload)) {
          return;
        }
        skipNextPasteEventRef.current = true;
        pasteObjects(payload);
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const onPaste = (e) => {
      if (readOnlyRef.current) return;
      if (!shouldHandleInvitationBuilderClipboard(e)) return;

      if (skipNextPasteEventRef.current) {
        skipNextPasteEventRef.current = false;
        return;
      }

      let payload = clipboardPayloadRef.current;
      if (!isOurClipboardPayload(payload)) {
        const text = e.clipboardData?.getData("text/plain");
        if (!text) return;
        try {
          const parsed = JSON.parse(text);
          if (isOurClipboardPayload(parsed)) {
            payload = parsed;
          } else {
            return;
          }
        } catch {
          return;
        }
      }

      e.preventDefault();
      e.stopPropagation();
      pasteObjects(payload);
    };

    if (!readOnly) {
      window.addEventListener("keydown", onKeyDown, true);
      window.addEventListener("paste", onPaste, true);
    }

    return () => {
      cancelAnimationFrame(videoRaf);
      canvas.off("object:added", scheduleVideoScan);
      canvas.off("object:removed", scheduleVideoScan);
      if (!readOnly) {
        window.removeEventListener("keydown", onKeyDown, true);
        window.removeEventListener("paste", onPaste, true);
      }
      canvas.dispose();
      fabricRef.current = null;
    };
  }, [backgroundColor, readOnly]);

  useImperativeHandle(
    ref,
    () => ({
      getCanvas: () => fabricRef.current,

      getCanvasJson: () => {
        const c = fabricRef.current;
        return c ? c.toJSON() : null;
      },

      loadFromJson: async (json) => {
        const c = fabricRef.current;
        if (!c || json == null) {
          throw new Error("Canvas not ready");
        }
        const data = typeof json === "string" ? JSON.parse(json) : json;
        const clean = sanitizeCanvasJson(data);
        c.clear();
        if (clean.width) c.set("width", clean.width);
        if (clean.height) c.set("height", clean.height);
        if (clean.background) c.backgroundColor = clean.background;
        await c.loadFromJSON(clean);
        if (readOnlyRef.current) {
          applyReadOnlyToCanvas(c);
        }
        const fontFamilies = collectFontFamiliesFromFabricCanvas(c);
        await loadInvitationGoogleFonts(fontFamilies);
        c._pingviteHasVideo = countInvitationVideosOnCanvas(c) > 0;
        playInvitationVideosOnCanvas(c);
        c.requestRenderAll();
        cbRef.current.onSelectionChange?.(c.getActiveObject() ?? null);
        cbRef.current.onCanvasChange?.();
      },

      clearCanvas: () => {
        const c = fabricRef.current;
        if (!c) return;
        c.clear();
        c.backgroundColor = backgroundColor;
        c._pingviteHasVideo = false;
        c.requestRenderAll();
        cbRef.current.onSelectionChange?.(null);
      },

      exportDataUrl: ({ format = "png", quality = 0.92, multiplier = 1 } = {}) => {
        const c = fabricRef.current;
        if (!c) return null;
        const fmt = format === "jpeg" || format === "jpg" ? "jpeg" : "png";
        return c.toDataURL({
          format: fmt,
          quality: fmt === "jpeg" ? quality : 1,
          multiplier: multiplier ?? 1,
        });
      },

      exportThumbnailBase64: () => {
        const c = fabricRef.current;
        if (!c) return null;
        return c.toDataURL({ format: "png", multiplier: 0.2, quality: 1 });
      },

      addText: (preset = "body") => {
        const c = fabricRef.current;
        if (!c) return;
        const presets = {
          heading: {
            text: "Heading",
            fontSize: 40,
            fontWeight: "bold",
            top: 120,
          },
          subheading: {
            text: "Subheading",
            fontSize: 28,
            top: 220,
          },
          body: {
            text: "Body text",
            fontSize: 18,
            top: 320,
          },
        };
        const p = presets[preset] || presets.body;
        const tb = new Textbox(p.text, {
          left: CANVAS_WIDTH / 2,
          top: p.top,
          originX: "center",
          width: 600,
          fontSize: p.fontSize,
          fontFamily: "Inter",
          fill: "#111827",
          textAlign: "center",
          fontWeight: p.fontWeight || "normal",
          editable: true,
          selectable: true,
          evented: true,
        });
        c.add(tb);
        c.setActiveObject(tb);
        c.requestRenderAll();
        cbRef.current.onSelectionChange?.(tb);
        cbRef.current.onCanvasChange?.();
      },

      addImageFromUrl: (url) => {
        const c = fabricRef.current;
        if (!c || !url) return Promise.resolve();

        const placeImage = (img) => {
          const scale = Math.min(
            (CANVAS_WIDTH * 0.85) / (img.width || 1),
            (CANVAS_HEIGHT * 0.5) / (img.height || 1),
            1
          );
          img.set({ scaleX: scale, scaleY: scale });
          img.set({
            left: CANVAS_WIDTH / 2,
            top: CANVAS_HEIGHT / 2,
            originX: "center",
            originY: "center",
          });
          c.add(img);
          c.setActiveObject(img);
          c._pingviteHasVideo = countInvitationVideosOnCanvas(c) > 0;
          c.requestRenderAll();
          cbRef.current.onSelectionChange?.(img);
          cbRef.current.onCanvasChange?.();
        };

        if (isWebmUrl(url)) {
          if (countInvitationVideosOnCanvas(c) >= 2) {
            return Promise.reject(new MaxInvitationVideosError());
          }
          return loadInvitationFabricVideoFromUrl(url).then((videoEl) => {
            const vid = new InvitationFabricVideo(videoEl, {
              crossOrigin: "anonymous",
            });
            placeImage(vid);
            vid.tryPlay();
          });
        }

        return loadFabricImageForCanvas(url).then(placeImage);
      },

      discardSelection: () => {
        const c = fabricRef.current;
        if (!c) return;
        c.discardActiveObject();
        c.requestRenderAll();
        cbRef.current.onSelectionChange?.(null);
      },
    }),
    [backgroundColor]
  );

  return (
    <div
      className={`invitation-builder__canvas-wrap${
        readOnly ? " invitation-builder__canvas-wrap--readonly" : ""
      }`}
    >
      <canvas ref={canvasEl} className="invitation-builder__canvas" />
    </div>
  );
});

export default CanvasArea;
